import { Text, ToDosIcon } from '@lmc/design-system'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import StepEntryCard, {
  StepEntryCardItem,
  StepEntryCardRow
} from '../../StepEntryCard'
import { DATE_FORMAT, TIME_FORMAT } from '../../stepLayout'
import { fromDateOnlyIso } from '../../stepValues'
import { STATUS_LABEL_KEYS } from './FollowUpActionDrawer'
import { type FollowUpActionValue, isOverdue } from './followUpActionValues'

type FollowUpActionCardProps = {
  action: FollowUpActionValue
  onDelete: () => void
  onEdit: () => void
  ownerName?: string
  testId: string
}

/** One action as designed: what it is, who owns it and where it stands. */
const FollowUpActionCard = ({
  action,
  onDelete,
  onEdit,
  ownerName,
  testId
}: FollowUpActionCardProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const notSet = t(incidentReportsConstants.CASE_V2_ACTIONS_NOT_SET)
  const dueDate = fromDateOnlyIso(action.dueDate)?.format(DATE_FORMAT)
  const completedAt = action.completedAt
    ? dayjs(action.completedAt).format(`${DATE_FORMAT} ${TIME_FORMAT}`)
    : undefined

  return (
    <StepEntryCard
      deleteLabel={t(incidentReportsConstants.CASE_V2_ACTIONS_DELETE, {
        title: action.title
      })}
      description={action.details}
      editLabel={t(incidentReportsConstants.CASE_V2_ACTIONS_EDIT, {
        title: action.title
      })}
      icon={<ToDosIcon size={24} />}
      onDelete={onDelete}
      onEdit={onEdit}
      testId={testId}
      title={action.title}
    >
      <StepEntryCardRow>
        <StepEntryCardItem
          label={t(incidentReportsConstants.CASE_V2_ACTIONS_OWNER)}
          testId={`${testId}-owner`}
        >
          {ownerName ?? notSet}
        </StepEntryCardItem>
        <StepEntryCardItem
          label={t(incidentReportsConstants.CASE_V2_ACTIONS_STATUS)}
          testId={`${testId}-status`}
        >
          {t(STATUS_LABEL_KEYS[action.status])}
        </StepEntryCardItem>
        <StepEntryCardItem
          label={t(incidentReportsConstants.CASE_V2_ACTIONS_DUE_DATE)}
          testId={`${testId}-due-date`}
        >
          {dueDate && isOverdue(action) ? (
            <Text as="span" weight="medium" tone="critical">
              {t(incidentReportsConstants.CASE_V2_ACTIONS_OVERDUE, {
                date: dueDate
              })}
            </Text>
          ) : (
            (dueDate ?? notSet)
          )}
        </StepEntryCardItem>
        <StepEntryCardItem
          label={t(incidentReportsConstants.CASE_V2_ACTIONS_COMPLETION_TIME)}
          testId={`${testId}-completed-at`}
        >
          {completedAt ?? notSet}
        </StepEntryCardItem>
      </StepEntryCardRow>
    </StepEntryCard>
  )
}

export default FollowUpActionCard
